import { AddOmitRecord, PropertyRecord, Appeal } from '../../../commontypes/types';

const nonNumericBlockLot = (internalFormat: string): boolean => {
  const ccddBlockLot = internalFormat.substring(0, 22);
  return !ccddBlockLot.match(/[0-9 ]+/);
};

const isBlank = (str: string | null | undefined): boolean => {
  return (!str || /^\s*$/.test(str));
};

const validateInternalBLQ = (input: string | null | undefined): boolean => {
  if (input === undefined || input === null) {
    return false;
  } else if (input.length < 22) {
    return false;
  } else if (isBlank(input)) {
    return false;
  } else if (nonNumericBlockLot(input)) {
    return false;
  }
  return true;
};

const removeLeadingZeros = (input: string): string => {
  return trimChar(input, "0");
};

const removeLeadingSpaces = (input: string): string => {
  return trimChar(input, " ");
};

const trimChar = (string: string, charToRemove: string): string => {
  while (string.charAt(0) === charToRemove) {
    string = string.substring(1);
  }
  return string;
};

const blockLotFormatter = (left: string, right: string): string => {
  if (isBlank(right)) {
    return removeLeadingZeros(left);
  } else {
    return removeLeadingZeros(left) + "." + removeLeadingSpaces(right);
  }
};

export const toBLQDisplayFormat = (internalFormat: string): string => {
  if (!validateInternalBLQ(internalFormat)) {
    return "";
  } else {
    const blockLeft = internalFormat.substring(4, 9);
    const blockRight = internalFormat.substring(9, 13);
    const lotLeft = internalFormat.substring(13, 18);
    const lotRight = internalFormat.substring(18, 22);
    const qualifier = internalFormat.substring(22);

    let blqDisplayFormat = blockLotFormatter(blockLeft, blockRight) + "-" + blockLotFormatter(lotLeft, lotRight);
    let qualifierTrimmed = qualifier.trim();
    if (qualifierTrimmed !== "") {
      blqDisplayFormat = blqDisplayFormat + "-" + qualifierTrimmed;
    }

    return blqDisplayFormat;
  }
};

const stringToNumber = (x: string): number => {
  const parsed = parseInt(x, 10);
  if (isNaN(parsed)) {
    return 0;
  } else {
    return parsed;
  }
};

const toFullYear = (mm: string): string => {
  const currentYear = new Date().getFullYear().toString().substr(-2);
  const currentYearNum = parseInt(currentYear, 10);
  const mmNum = parseInt(mm, 10);
  if ((currentYearNum - mmNum) >= 0) {
    return "20" + mm;
  } else {
    return "19" + mm;
  }
};

const formatDate = (input: string | null | undefined): string => {
  if (input === undefined || input === null || input === "000000") {
    return "";
  } else {
    // MMDDYY
    const month = input.substring(0, 2);
    const day = input.substring(2, 4);
    const year = input.substring(4, 6);

    return month + "/" + day + "/" + toFullYear(year);
  }
};

const get10PropertyInfo = (tenData: string, blq: string): AddOmitRecord => {
  const type = tenData.substring(47, 62).trim();
  const propertyClass = tenData.substring(74, 77).trim();
  const additionLand = Number(tenData.substring(77, 86));
  const additionImprovement = Number(tenData.substring(86, 95));
  //const additionAbatement = 0;
  const additionAbatement = Number(tenData.substring(98, 104));
  const additionTotal = Number(tenData.substring(104, 113));
  const additionProrated = Number(tenData.substring(113, 122));
  const taxYear = Number.parseInt(tenData.substring(36, 40));
  const completionDate = tenData.substring(40, 44);
  const totalMonthsAssessed = tenData.substring(44, 46);
  const listType = tenData.substring(35, 36);

  return {
    blq,
    type,
    additionLand,
    additionImprovement,
    additionAbatement,
    additionProrated,
    additionTotal,
    listType,
    taxYear,
    completionDate,
    totalMonthsAssessed,
    propertyClass: {
      key: propertyClass,
      label: getPropertyClassLabel(propertyClass)
    },
  };
};

const getPropertyClassLabel = (propertyClassCode: string): string => {
  switch (propertyClassCode) {
    case '1':
      return 'Vacant';
    case '2':
      return 'Residential';
    case '3A':
      return 'Farm - Regular';
    case '3B':
      return 'Farm - Qualified';
    case '4A':
      return 'Commercial';
    case '4B':
      return 'Industrial';
    case '4C':
      return 'Apartments - 5 or more families';
    case '6A':
      return 'Personal Property';
    case '6B':
      return 'Machinery, Apparatus or Equipment of Petroleum Refineries';
    case '15A':
      return 'Public School Property';
    case '15B':
      return 'Other School Property';
    case '15C':
      return 'Public Property';
    case '15D':
      return 'Church Charitable Property';
    case '15E':
      return 'Cemeteries and Graveyards';
    case '15F':
      return 'Other Exempt Properties';
    default:
      return 'UNKNOWN: <' + propertyClassCode + ">";
  }
};

const getPropertyInfo = (mod4Data: string, modIV10Rows?: string[]): PropertyRecord => {
  const ccddBlqParam = mod4Data.substring(0, 33);
  const blq = toBLQDisplayFormat(ccddBlqParam);

  const ownerField = mod4Data.substring(175, 210);
  const owner = ownerField ? ownerField.trim() : "";

  const relevantPropertyAddress = mod4Data.substring(58, 83);
  const relevantProperty = relevantPropertyAddress ? relevantPropertyAddress.trim() : "";

  const addressField = mod4Data.substring(210, 235);
  const address = addressField ? addressField.trim() : "";

  const cityStateField = mod4Data.substring(235, 260);
  const cityState = cityStateField ? cityStateField.trim() : "";

  const zipField = mod4Data.substring(260, 265);
  const zip = zipField ? zipField.trim() : "";

  const land = mod4Data.substring(420, 429);
  const buildingImprovement = mod4Data.substring(429, 438);
  const totalNum = mod4Data.substring(438, 447);
  const landNum = stringToNumber(land);
  const buildingImprovementNum = stringToNumber(buildingImprovement);
  const total = stringToNumber(totalNum);

  const exemption1 = mod4Data.substring(460, 468);
  const exemption2 = mod4Data.substring(469, 477);
  const exemption3 = mod4Data.substring(478, 486);
  const exemption4 = mod4Data.substring(487, 495);

  const exemption1Num = stringToNumber(exemption1);
  const exemption2Num = stringToNumber(exemption2);
  const exemption3Num = stringToNumber(exemption3);
  const exemption4Num = stringToNumber(exemption4);

  const purchasePriceField = mod4Data.substring(312, 321);
  const purchasePriceNum = stringToNumber(purchasePriceField);

  const lotSizeField = mod4Data.substring(98, 118);
  const lotSizeNum = lotSizeField.trim();

  const abatementNum = (exemption1Num + exemption2Num + exemption3Num + exemption4Num);

  const purchaseDateField = mod4Data.substring(306, 312);
  const purchaseDate = formatDate(purchaseDateField);

  const propertyClassField = mod4Data.substring(55, 58);
  const propertyClass = propertyClassField.trim();

  const tenData: AddOmitRecord[] | null = modIV10Rows ? [] : null;
  if (modIV10Rows) {
    modIV10Rows.forEach(mod410Data => {
      if (blq === toBLQDisplayFormat(mod410Data.substring(0, 33))) {
        const tenPropertyInfo = get10PropertyInfo(mod410Data, blq);
        if (tenPropertyInfo.listType !== "D") {
          (tenData as AddOmitRecord[]).push(tenPropertyInfo);
          if ((tenData as AddOmitRecord[]).length > 1) {
            console.log("Multiple 10 records for " + blq);
          }
        }
      }
    });
  }

  return {
    key: blq,
    blq: blq,
    owner: owner,
    relevantProperty: relevantProperty,
    address: address,
    cityState: cityState,
    zip: zip,
    classification: {
      key: propertyClass,
      label: getPropertyClassLabel(propertyClass)
    },
    lotSize: lotSizeNum,
    purchasePrice: purchasePriceNum,
    purchaseDate: purchaseDate,
    currentAssessment: {
      land: landNum,
      buildingImprovement: buildingImprovementNum,
      abatement: abatementNum,
      total: total
    },
    tenData: tenData
  };
};

const findAppealForBLQ = (appeals: Appeal[], blq: string): Appeal | undefined =>
  appeals.find(a => a.APPEAL.property.blq === blq);

export const searchModIVForBLQ = (
  modIVRows: string[],
  block: string | null,
  lot: string | null,
  qualifier: string | null,
  modIV10Rows?: string[],
  requireAddition?: boolean,
  existingAppeals?: Appeal[]
): PropertyRecord[] => {
  const matches: PropertyRecord[] = [];

  if (!block || !lot) {
    return matches;
  }

  const blockToMatch = block.replace(/\./g, '');
  const lotToMatch = lot.replace(/\./g, '');
  const qualifierToMatch = qualifier ? qualifier.toUpperCase() : '';

  for (const modIVRow of modIVRows) {
    const parsedBlock = modIVRow.substring(4, 13).replace(/\s/g, '');
    const parsedLot = modIVRow.substring(13, 22).replace(/\s/g, '');
    const parsedQualifier = modIVRow.substring(22, 33).replace(/\s/g, '').toUpperCase();

    if (parsedBlock.replace(/^0+/, '') === blockToMatch &&
      parsedLot.replace(/^0+/, '') === lotToMatch &&
      (parsedQualifier.includes(qualifierToMatch) || qualifier === undefined)) {
      const match = getPropertyInfo(modIVRow, modIV10Rows);
      if (!requireAddition ||
        (match.tenData && match.tenData.length > 0) ||
        (existingAppeals && findAppealForBLQ(existingAppeals, toBLQDisplayFormat(modIVRow.substring(0, 33))))) {
        matches.push(match);
      }
    }
  }
  return matches;
};

export const searchModIVForStreet = (
  modIVRows: string[],
  street: string | null,
  modIV10Rows?: string[],
  requireAddition?: boolean,
  existingAppeals?: Appeal[]
): PropertyRecord[] => {
  const matches: PropertyRecord[] = [];

  if (!street) {
    return matches;
  }

  for (const modIVRow of modIVRows) {
    const modIVAddress = modIVRow.substring(58, 83).replace(/\s/g, '');
    if (modIVAddress.includes(street.toUpperCase().replace(/\s/g, ''))) {
      const match = getPropertyInfo(modIVRow, modIV10Rows);
      if (!requireAddition ||
        (match.tenData && match.tenData.length > 0) ||
        (existingAppeals && findAppealForBLQ(existingAppeals, toBLQDisplayFormat(modIVRow.substring(0, 33))))) {
        matches.push(match);
      }
    }
  }
  return matches;
};